import React, { createContext, useContext } from 'react';
import { useAuth } from './AuthContext';
import { useAgUiChat } from '../hooks/useAgUiChat';
import { useChatHistory } from '../hooks/useChatHistory';
import { AgUiMessage, AgUiToolCall, ChatThread } from '../types/agui';

interface AgentSessionContextType {
  messages: AgUiMessage[];
  activeToolCalls: AgUiToolCall[];
  sessionState: Record<string, any>;
  isStreaming: boolean;
  error: string | null;
  threadId: string;
  targetAgentId: string;
  setTargetAgentId: (id: string) => void;
  sendMessage: (text: string) => Promise<void>;
  clearChat: () => void;
  switchThread: (threadId: string) => Promise<void>;
  threads: ChatThread[];
  loadingHistory: boolean;
  historyError: string | null;
  deleteThread: (threadId: string) => Promise<void>;
  syncStatus: 'synced' | 'pending' | 'syncing' | 'error';
  lastSyncedAt: Date | null;
  idleMinutes: number;
  syncNow: () => Promise<void>;
}

const AgentSessionContext = createContext<AgentSessionContextType | undefined>(undefined);

export const AgentSessionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { currentUser } = useAuth();
  const agUi = useAgUiChat({ currentUser });
  const history = useChatHistory(currentUser?.uid);

  const value: AgentSessionContextType = {
    ...agUi,
    threads: history.threads,
    loadingHistory: history.loading,
    historyError: history.error,
    deleteThread: history.deleteThread,
  };

  return (
    <AgentSessionContext.Provider value={value}>
      {children}
    </AgentSessionContext.Provider>
  );
};

export const useAgentSession = (): AgentSessionContextType => {
  const ctx = useContext(AgentSessionContext);
  if (!ctx) {
    throw new Error('useAgentSession must be used within an AgentSessionProvider');
  }
  return ctx;
};
